import axios from "axios";
import store from "../store";
import useFunctions from "./functions1";
import moment from "moment";
import { $api } from "@/services/api1";
import useFormatter from "@/use/formatter";
import { SearchTypes, Operators } from "@/types/advanced-search";
import { ApplicantTypes } from "@/types/applicant";

const { decodeHTMLEncodedStr } = useFunctions();
const { formatPhoneNumber } = useFormatter();

function getURLParameter(parameterName) {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const paramValue = urlParams.get(parameterName);

  return paramValue ?? "unknown";
}

function isValidEmail(email) {
  // check if email is valid using a regular expression
  var regex = new RegExp(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/);
  return regex.test(email);
}

function isLowerEnvironment() {
	 var host = window.location.hostname;
	 var port = window.location.port;
	 var hosts = ["-qa", "-uat", "-ci"];
	 var localHostPorts = ["44370", "44375", "44382"];

	 if ((host.includes("localhost") && localHostPorts.includes(port))) {
			return true;
	 }

	 host = "app-lz02-qa-losptl-eus.azurewebsites.net";
	 return hosts.some(h => host.includes(h));
}

function isValidSSN(ssn) {
  // remove all hyphens and spaces from the ssn
  ssn = ssn?.replace(/[- ]/g, "");

  // a valid 9 digit ssn is [3 digits not including 000, 666, 900-999][2 digits 01-99][4 digits 0001-9999]
  var regex = new RegExp(/^(?!666|000|9\d{2})\d{3}(?!00)\d{2}(?!0{4})\d{4}$/);

	if (isLowerEnvironment()) {
		// In development, allows 666 as valid SSN for testing purposes -- not including 000, 900-999
		regex = new RegExp(/^(?!000|9\d{2})\d{3}(?!00)\d{2}(?!0{4})\d{4}$/);
	}
	
  // check if the ssn is valid using the regular expression, return true or false
  return regex.test(ssn);
}

function isValidCLUID(searchText) {
  return /^[A-Za-z0-9]{17}$/.test(searchText);
}

function phoneFormat(phone) {
  // if the phone number length is 10 digits, then add dashes to it, otherwise return the phone unchanged
  return phone.length === 10
    ? `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6)}`
    : phone;
}

function ssnFormat(ssn) {
  // if the ssn length is 9 digits and has five stars, change the ssn format to XXX-XX-0000
  return ssn?.length === 9 && ssn?.includes("*****")
    ? ssn.replace("*****", "XXX-XX-")
    : ssn;
}

function capitalize(input) {
  // this capitalizes each word of a string
  return input
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.substring(1))
    .join(" ");
}

function formatFromUtc(timestamp, format = "MMMM D, YYYY") {
  var notAvailable = "No Date Available";

  if (typeof timestamp !== "string" || !moment(timestamp).isValid()) {
    return notAvailable;
  }

  return moment.utc(timestamp).format(format) || notAvailable;
}

function compareObjects(obj1, obj2, path = []) {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // description: takes an input of two objects and returns an array of all the differences between the two //
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////
  let mismatchedKeys = [];
  if (typeof obj1 !== typeof obj2) {
    if (obj1 !== obj2) {
      mismatchedKeys.push(path);
    }
    return mismatchedKeys;
  }

  if (typeof obj1 !== "object" || obj1 === null) {
    if (obj1 !== obj2) {
      mismatchedKeys.push(path);
    }
    return mismatchedKeys;
  }

  for (const key of Object.keys(obj1)) {
    let newPath = [...path, key];
    // eslint-disable-next-line no-prototype-builtins
    if (!obj2.hasOwnProperty(key)) {
      mismatchedKeys.push(newPath);
    } else {
      mismatchedKeys = [
        ...mismatchedKeys,
        ...compareObjects(obj1[key], obj2[key], newPath),
      ];
    }
  }
  return mismatchedKeys;
}

function compareIncomeObjects(obj1, obj2, path = []) {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // description: takes an input of two objects and returns an array of all the differences between the two //
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////
  let mismatchedKeys = [];

  if (typeof obj1 !== typeof obj2) {
    return mismatchedKeys;
  }

  if (typeof obj1 !== "object" || obj1 === null) {
    if (obj1 !== obj2) {
      mismatchedKeys.push(path);
    }
    return mismatchedKeys;
  }

  for (const key of Object.keys(obj1)) {
    let newPath = [...path, key];
    // eslint-disable-next-line no-prototype-builtins
    if (!obj2.hasOwnProperty(key)) {
      mismatchedKeys.push(newPath);
    }
    // if
    else if (
      (obj1[key] === null) & (obj2[key] !== null) ||
      (obj1[key] !== null) & (obj2[key] === null)
    ) {
      mismatchedKeys.push(newPath);
    } else {
      mismatchedKeys = [
        ...mismatchedKeys,
        ...compareObjects(obj1[key], obj2[key], newPath),
      ];
    }
  }
  return mismatchedKeys;
}

function getLenderName(lenderId, lenderName) {
	if (lenderName) {
		return lenderName;
	}
  if (store.state.types["Lenders"]) {
    return (
      store.state.types["Lenders"].filter(
        (lender) => lender.id === Number(lenderId),
      )?.[0]?.name || "No Lender Provided"
    );
  } else {
    return "No Lender Provided";
  }
}

function displayProductType(typeId) {
  if (store.state.types["Products"]) {
    return (
      store.state.types["Products"].results.filter(
        (product) => product.id == typeId,
      )?.[0]?.name || ""
    );
  } else {
    return "";
  }
}
function convertTypeIdToTypeName(type, typeId, property) {
  let types = store.state.types[type].results;
  const id = typeof typeId === "string" ? parseInt(typeId) : typeId;

  if (type === "Schools" || type === "GraduateDegreePrograms") {
    types = store.state.types[type];
  }
  const name = types.find((t) => t.id === id)?.name;
  return name;
}

function isNumeric(str) {
  if (typeof str != "string") return false; // we only process strings!
  return (
    !isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
    !isNaN(parseFloat(str))
  ); // ...and ensure strings of whitespace fail
}

function checkIncomeFormChanges(object1, object2, options) {
  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // description: returns an html string of changes in a form by comparing the before object with the after object //
  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  let html = "";

  compareIncomeObjects(object1, object2).forEach((reference) => {
    let before = reference.reduce((o, key) => o[key], object2);
    let after = reference.reduce((o, key) => o[key], object1);
    let key = reference[0];

    if (options?.keysToSkip?.includes(key)) {
      return;
    }

    const rename = options?.keysToRename?.find((k) => k.key == key)?.rename;

    if (before === undefined || before === null || before === "") {
      before = null;
    }

    if (after === undefined || after === null || after === "") {
      after = null;
    }

    if (before === after) {
      return;
    } else if (
      reference[0] === "incomeTotal" ||
      reference[0] === "isEditing" ||
      reference[0] === "incomePeriodTypeId"
    ) {
      return;
    } else {
      if (rename) {
        reference[0] = rename;
      }

      if (before === undefined || before === null) {
        before = "N/A";
      }

      if (after === undefined || after === null) {
        after = "N/A";
      }

      // get the last variable from the reference array, and convert the camelCase to add spaces
      var name = reference.pop().replace(/([a-z])([A-Z])/g, `$1 $2`);

      // Handle amount
      html += `<tr><td class="xbefore">${name}</td><td class="xafter">${before}</td><td class="xafter">${after}</td></tr>`;
    }
  });

  return html === "" ? false : `<table class='xmodal-changes'>${html}</table>`;
}

function checkFormChanges(object1, object2, options) {
  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // description: returns an html string of changes in a form by comparing the before object with the after object //
  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  var html = "";

  compareObjects(object1, object2).forEach((reference) => {
    var before = reference.reduce((o, key) => o[key], object2);
    var after = reference.reduce((o, key) => o[key], object1);
    var key = reference[0];

    if (options?.keysToSkip?.includes(key)) {
      return;
    }

    var rename = options?.keysToRename?.find((k) => k.key == key)?.rename;

    if (before === undefined || before === null || before === "") {
      before = null;
    }

    if (after === undefined || after === null || after === "") {
      after = null;
    }

    if (before === after) {
      // do not add to table if the values are the same
      return;
    }

    if (key === "phoneNumber" || key === "preferredPhoneNumber") {
      const normalizedBefore = before.replace(/[^0-9]/g, "");
      const normalizedAfter = after.replace(/[^0-9]/g, "");

      if (normalizedBefore === normalizedAfter) {
        return;
      }

      const formattedBefore = formatPhoneNumber(normalizedBefore);
      const formattedAfter = formatPhoneNumber(normalizedAfter);

      before = formattedBefore;
      after = formattedAfter;
    }

    if (rename) {
      reference[0] = rename;
    }

    if (before === null) {
      before = "N/A";
    }

    if (after === null) {
      after = "N/A";
    }
    // get the last variable from the reference array, and convert the camelCase to add spaces
    var name = reference.pop().replace(/([a-z])([A-Z])/g, `$1 $2`);

    html += `<tr><td class="xbefore">${name}</td><td class="xafter">${before}</td><td class="xafter">${after}</td></tr>`;
  });

  return html?.length > 0
    ? `<table class='xmodal-changes'>${html}</table>`
    : null;
}

function buildFormChangesTable(changes, mode) {
  var html = "";

  changes.forEach((change) => {
    if (mode === "add") {
      html += `<tr><td class="xbefore">${change.name}</td><td class="xafter">${change.after}</td></tr>`;
    } else {
      html += `<tr><td class="xbefore">${change.name}</td><td class="xafter">${change.before}</td><td class="xafter">${change.after}</td></tr>`;
    }
  });

  return html?.length > 0
    ? `<table class='xmodal-changes'>${html}</table>`
    : null;
}

function summarizeFormChanges(object1, object2, options) {
  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // description: returns an html string of changes in a form by comparing the before object with the after object //
  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  var changes = [];

  if (options?.mode === "add") {
    const keys = Object.keys(object1);
    keys.forEach((key) => {
      if (options?.keysToSkip?.includes(key)) {
        return;
      }

      var rename = options?.keysToRename?.find((k) => k.key == key)?.rename;

      let name = key.replace(/([a-z])([A-Z])/g, `$1 $2`);

      if (rename) {
        name = rename;
      }

      var before = object2[key];
      var after = object1[key];

      after =
        after === undefined || after === null || after === "" ? "N/A" : after;
      before =
        before === undefined || before === null || before === ""
          ? "N/A"
          : before;

      changes.push({
        after: after,
        before: before,
        comment: `${capitalize(name)} : ${after}`,
        key: key,
        name: capitalize(name),
      });
    });
  } else {
    compareObjects(object1, object2).forEach((reference) => {
      var before = reference.reduce((o, key) => o[key], object2);
      var after = reference.reduce((o, key) => o[key], object1);
      var key = reference[0];

      if (options?.keysToSkip?.includes(key)) {
        return;
      }

      var rename = options?.keysToRename?.find((k) => k.key == key)?.rename;

      if (before === undefined || before === null || before === "") {
        before = null;
      }

      if (after === undefined || after === null || after === "") {
        after = null;
      }

      if (before === after) {
        // do not add to table if the values are the same
        return;
      }

      if (rename) {
        reference[0] = rename;
      }

      if (before === null) {
        before = "N/A";
      }

      if (after === null) {
        after = "N/A";
      }

      // get the last variable from the reference array, and convert the camelCase to add spaces
      var name = reference.pop().replace(/([a-z])([A-Z])/g, `$1 $2`);
      var change;

      if (options.mode === "add") {
        change = {
          key,
          name: capitalize(name),
          before,
          after,
          comment: `${capitalize(name)}: ${after}`,
        };
      } else {
        change = {
          key,
          name: capitalize(name),
          before,
          after,
          comment: `${capitalize(name)}: from ${before} to ${after}`,
        };
      }

      changes.push(change);
    });
  }

  // For each change
  var filteredChanges = changes.filter(
    (change) =>
      !(options?.keysToExcludeFromSummaryComment || []).includes(change.key),
  );
  var lastIndex = filteredChanges.length - 1;

  var changeSummaries = filteredChanges
    .map((change, index) => {
      if (index === lastIndex) {
        return change.comment + ".";
      } else if (index === 0) {
        return change.comment + " | ";
      } else {
        return change.comment + " " + " | ";
      }
    })
    .join("");

  var fieldsChanged = filteredChanges
    .map((change, index) => {
      if (filteredChanges.length === 1) {
        return change.name;
      } else if (filteredChanges.length === 2) {
        return index === lastIndex ? change.name : `${change.name} and `;
      } else {
        if (index === lastIndex) {
          return `and ${change.name}`;
        } else {
          return change.name + ", ";
        }
      }
    })
    .join("");

  var errorMessage = `${fieldsChanged} change${
    filteredChanges.length > 1 ? "s" : ""
  } could not be saved.`;

  var comment = `${options?.commentPrefix} ${changeSummaries}`;

  return {
    changes,
    comment,
    commentPrefix: options?.commentPrefix,
    fieldsChanged,
    errorMessage,
    userDisplayName: options?.userDisplayName,
  };
}

function checkLocalStorage(key, expirationTime = 86400000) {
  ///////////////////////////////////////////////////////////////////////////////////////////
  // description: checks the localStorage to determine if an item exits and is not expired //
  ///////////////////////////////////////////////////////////////////////////////////////////

  // if the localstore has the type
  if (localStorage.getItem(key)) {
    var localcache = JSON.parse(localStorage.getItem(key));

    // if the data has been cached for less than expirationTime (default 1 day (86400000 ms))
    if (Date.now() - localcache.timestamp < expirationTime) {
      return localcache;
    }

    //if not clear it
    localStorage.removeItem(key);
  }

  // return as false if localstorage check fails
  return false;
}

async function getLenders() {
  var localCache = checkLocalStorage(`cache.Lenders`);

  // if lender data is stored in localstorage
  if (localCache) {
    store.state.types["Lenders"] = localCache.data;
    return localCache.data;
  } else {
    await axios(`/api/lenders`, { require: "json" })
      .then((response) => {
        if (Array.isArray(response.data) && response.data.length > 0) {
          // add the data to localstorage
          localStorage.setItem(
            `cache.Lenders`,
            JSON.stringify({ timestamp: Date.now(), data: response.data }),
          );

          store.state.types["Lenders"] = response.data;
          return response.data;
        }
      })
      .catch((error) => {
        console.log(`API Error: /api/lenders`, error);
        return false;
      });
  }
}

async function getType(type, programTypeId = null) {
  const key = programTypeId == null ? type : `${type}${programTypeId}`;
  const localData = checkLocalStorage(`cache.${key}`);

  const query = programTypeId !== null ? { programTypeId } : {};

  // if the type is stored in local storage
  if (localData) {
    store.state.types[key] = localData.data;
  } else {
    try {
      const response = await axios.get(`/api/types/${type}`, {
        params: query,
        responseType: "json",
      });

      // add the data to local storage
      localStorage.setItem(
        `cache.${key}`,
        JSON.stringify({ timestamp: Date.now(), data: response.data }),
      );

      store.state.types[key] = response.data;
    } catch (error) {
      console.log(`API Error: /api/types/${type}`, error);
      return false;
    }
  }
}

async function getSchoolsByProgram(programTypeId, productTypeId, lenderId) {
  const key = "Schools";

  try {
    const response = await $api.schools.getSchoolsByProgram(
      programTypeId,
      productTypeId,
      lenderId,
    );

    const schools = response.data.map((school) => {
      return {
        id: school.id,
        name: school.schoolName,
        code: school.schoolDOEId,
        branch: school.schoolBranchDOEId,
        detailId: school.schoolDetailsId,
      };
    });

    localStorage.setItem(
      `cache.${key}`,
      JSON.stringify({ timestamp: Date.now(), data: schools }),
    );

    store.state.types[key] = schools;
  } catch (error) {
    console.log(`API Error: /api/schools`, error);
    return false;
  }
}

async function getSchools() {
  const key = "Schools";

  try {
    const localData = checkLocalStorage(`cache.${key}`);

    if (localData) {
      store.state.types[key] = localData.data;
    } else {
      const response = await axios.get("/api/schools");

      const schools = response.data.map((school) => {
        return {
          id: school.id,
          name: school.schoolName,
          code: school.schoolDOEId,
          branch: school.schoolBranchDOEId,
        };
      });

      localStorage.setItem(
        `cache.${key}`,
        JSON.stringify({ timestamp: Date.now(), data: schools }),
      );

      store.state.types[key] = schools;
    }
  } catch (error) {
    console.log(`API Error: /api/schools`, error);
    return false;
  }
}

function cleanSearchText(text) {
  // remove all characters which are not a letter, number, space, period, comma, apostrophe, hyphen, plus, or @ (then remove double spaces)
  return text
    ? text.replace(/[^a-zA-Z0-9 .,'-@+]/g, "").replace(/\s+/g, " ")
    : "";
}

function getSearchParameters(searchText, searchFilters, pageNumber, searchCategory) {
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // description: this takes the search text and all active filters and outputs parameters for the url and post body //
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  var parameters = {};
  var searchType = null;

  // if the user is searching
  if (searchText) {
    var searchTypeParam = getURLParameter("SearchType");
    var searchOperationParam = getURLParameter("SearchOperation");
		
    var exactMatchParams = [
      SearchTypes.CLUID,
      SearchTypes.SSN,
      SearchTypes.NAME_EXACT,
      SearchTypes.PRIMARY_APPLICANT_NAME_EXACT,
      SearchTypes.CO_APPLICANT_NAME_EXACT,
    ];
    var partialMatchParams = [
      SearchTypes.APPLICATION_ID,
      SearchTypes.EMAIL,
      SearchTypes.PRIMARY_APPLICANT_EMAIL,
      SearchTypes.CO_APPLICANT_EMAIL,
      SearchTypes.NAME,
      SearchTypes.PRIMARY_APPLICANT_NAME,
      SearchTypes.CO_APPLICANT_NAME,
    ];
    var primaryApplicantParams = [
      SearchTypes.PRIMARY_APPLICANT_EMAIL,
      SearchTypes.PRIMARY_APPLICANT_NAME,
      SearchTypes.PRIMARY_APPLICANT_NAME_EXACT,
    ];
    var coApplicantParams = [
      SearchTypes.CO_APPLICANT_EMAIL,
      SearchTypes.CO_APPLICANT_NAME,
      SearchTypes.CO_APPLICANT_NAME_EXACT,
    ];

    var isValidSearchParam =
      searchTypeParam.toLowerCase() != "unknown" &&
      (exactMatchParams.includes(Number(searchTypeParam)) ||
        partialMatchParams.includes(Number(searchTypeParam)));

    if (
      isValidSearchParam &&
      searchOperationParam.toLowerCase() != "unknown" &&
      (Number(searchOperationParam) === 1 || Number(searchOperationParam) === 2)
    ) {
      parameters["SearchOperation"] = Number(searchOperationParam);
    } else {
      parameters["SearchOperation"] = null;
    }


    // make sure the user has typed at least 3 alphanumeric characters, and less than 200
    if (typeof searchText === "string" && (searchText || "").trim().length >= 1) {
			// clean the search text to remove multiple spaces and trim to clean up the ends
			searchText = searchText.replace(/\s+/g, ' ');

			// If no search type specified and not SSN or CLUID
			if (searchCategory && searchCategory.toLowerCase() === "applications" && !isValidSSN(searchText) && !isValidCLUID(searchText) && (searchTypeParam.toLowerCase() === "unknown" || (Number(searchTypeParam) === Number(SearchTypes.GENERIC_TEXT_INPUT)))) {
				searchType = "GenericTextInput";
				parameters["SearchType"] = Number(SearchTypes.GENERIC_TEXT_INPUT);
				parameters["SearchOperation"] = null;
				parameters["SearchTerms"] = searchText.split(" ");

				var encodedSearchText = encodeURIComponent(searchText);
				parameters["genericTextInput"] = encodedSearchText;
			}
      // check for partial email search first
      else if (
				searchCategory && searchCategory.toLowerCase() === "applications" &&
        searchTypeParam === SearchTypes.PRIMARY_APPLICANT_EMAIL.toString() ||
        searchTypeParam === SearchTypes.CO_APPLICANT_EMAIL.toString() ||
        searchTypeParam === SearchTypes.EMAIL.toString() ||
        (searchOperationParam.toLowerCase() == "unknown" &&
          searchText.includes("@"))
      ) {
        if (
          searchTypeParam === SearchTypes.PRIMARY_APPLICANT_EMAIL.toString() ||
          searchTypeParam === SearchTypes.CO_APPLICANT_EMAIL.toString()
        ) {
          parameters["SearchType"] = Number(searchTypeParam);
          searchType =
            searchTypeParam === SearchTypes.PRIMARY_APPLICANT_EMAIL.toString()
              ? "PrimaryApplicantEmailPartialMatch"
              : "CoApplicantEmailPartialMatch";
        } else {
          parameters["SearchType"] = SearchTypes.EMAIL;
          searchType = "Email";
        }

        parameters["SearchTerm"] = searchText;
        parameters["email"] = encodeURIComponent(searchText);
        parameters["SearchOperation"] = null;

        // if the user is searching by email [the search text will validate with the isValidEmail function]
      } else if (isValidEmail(searchText) && searchCategory && searchCategory.toLowerCase() === "applications") {
        searchType = "Email";
        parameters["SearchTerm"] = searchText;
        parameters["email"] = searchText;

        var isExactMatchEmail =
          searchTypeParam.toLowerCase() != "unknown" &&
          exactMatchParams.includes(Number(searchTypeParam));
        var isPartialMatchEmail =
          searchTypeParam.toLowerCase() != "unknown" &&
          partialMatchParams.includes(Number(searchTypeParam));

        // Overriding email search type from URL parameter
        if (isExactMatchEmail || isPartialMatchEmail) {
          parameters["SearchType"] = Number(searchTypeParam);
        } else {
          parameters["SearchType"] = SearchTypes.EMAIL;
        }

        // check for applicantType
        if (primaryApplicantParams.includes(Number(searchTypeParam))) {
          searchType = "PrimaryApplicantEmail";
        } else if (coApplicantParams.includes(Number(searchTypeParam))) {
          searchType = "CoApplicantEmail";
        }

        // if the user is searching by name [the search text will contain a letter as the first character]
      } else if (searchText.charAt(0).match(/[a-z]/i)) {
        // Overriding name search type from URL parameter
        if (
          searchTypeParam.toLowerCase() != "unknown" &&
          exactMatchParams.includes(Number(searchTypeParam))
        ) {
          searchType = "Name Exact Match";
          parameters["SearchType"] = Number(searchTypeParam);
          parameters["SearchTerms"] = searchText.split(" "); // Splits first and last name
          parameters["names"] = searchText;

          if (
            searchTypeParam ===
              SearchTypes.PRIMARY_APPLICANT_NAME_EXACT.toString() ||
            searchTypeParam === SearchTypes.CO_APPLICANT_NAME_EXACT.toString()
          ) {
            searchType =
              searchTypeParam ===
              SearchTypes.PRIMARY_APPLICANT_NAME_EXACT.toString()
                ? "PrimaryApplicantNameExactMatch"
                : "CoApplicantNameExactMatch";
          }
        } else if (
          searchTypeParam.toLowerCase() != "unknown" &&
          partialMatchParams.includes(Number(searchTypeParam))
        ) {
          searchType = "Name";
          parameters["SearchType"] = Number(searchTypeParam);
          parameters["SearchTerms"] = searchText.split(" ");
          parameters["names"] = searchText.split(" ");

          if (
            searchTypeParam === SearchTypes.PRIMARY_APPLICANT_NAME.toString() ||
            searchTypeParam === SearchTypes.CO_APPLICANT_NAME.toString()
          ) {
            searchType =
              searchTypeParam === SearchTypes.PRIMARY_APPLICANT_NAME.toString()
                ? "PrimaryApplicantNamePartialMatch"
                : "CoApplicantNamePartialMatch";
          }
        } else {
          // set the search type to name
          searchType = "Name";
          parameters["SearchType"] = SearchTypes.NAME;
          parameters["SearchTerms"] = searchText.split(" ");
          parameters["names"] = searchText.split(" ");

          // if two names present, go on exact match
          if (
            searchText.trim().split(" ").length >= 2 ||
            parameters["SearchOperation"] === Operators.EXACT_MATCH
          ) {
            parameters["SearchType"] = SearchTypes.NAME_EXACT;
          }
        }
        // id the user is searching by CLUID [the search text will contain 17 alphanumeric characters]
      } else if (
        isValidCLUID(searchText) ||
        (searchTypeParam.toLowerCase() !== "unknown" &&
          Number(searchTypeParam) === SearchTypes.CLUID)
      ) {
        searchType = "CLUID";
        parameters["SearchType"] = SearchTypes.CLUID;
        parameters["SearchTerm"] = searchText;
        parameters["cluid"] = searchText;

        // if the user is searching by ssn [the search text will validate with the isValidSSN function]
      } else if (
        isValidSSN(searchText) ||
        (searchTypeParam.toLowerCase() !== "unknown" &&
          Number(searchTypeParam) === SearchTypes.SSN)
      ) {
        // set the search type to ssn
        searchType = "SSN";
        parameters["SearchType"] = SearchTypes.SSN;
        parameters["SearchTerm"] = searchText;
        parameters["nationalidnumber"] = searchText;

        // if the user is searching by application id [the search text will contain an amount of numbers not equal to 9]
      } else {
        // set the search type to application id
        searchType = "Application ID";
        parameters["SearchType"] = SearchTypes.APPLICATION_ID;
        parameters["SearchTerm"] = searchText;
        parameters["referenceId"] = searchText;
      }
    } else {
      return { parameters: {}, searchType: "Unknown" };
    }
  }

  // if search filters are set
  if (searchFilters) {
    // loop through each of the filters
    searchFilters.forEach((section) => {
      // for each of the fields inside that filter
      section.results.forEach((field) => {
        // if the field has children
        if (field.children) {
          // for each of the children which are checked
          field.children
            .filter((child) => child.checked)
            .forEach((child) => {
              // check if the parameter exists, otherwise create it as a blank array
              parameters[section.param] = parameters[section.param] || [];

              // append the value to the parameter
              parameters[section.param].push(child.id);
            });

          // else if the field is checked
        } else if (field.checked && field.id != "skip") {
          // check if the parameter exists, otherwise create it as a blank array
          parameters[section.param] = parameters[section.param] || [];

          // append the value to the parameter
          parameters[section.param].push(field.id);
        }
      });
    });
  }

  // if there is a pagenumber
  if (pageNumber > 1) {
    parameters["PageNumber"] = pageNumber;
  }

  // return the search parameters
  return { parameters: parameters || {}, searchType: searchType || "Unknown" };
}

async function getApplications(searchText, searchFilters, pageNumber) {
  ////////////////////////////////////////////////////////////////////////////////////////////////
  // description: fetches applications by name, ssn, or application id and formats the response //
  ////////////////////////////////////////////////////////////////////////////////////////////////

  const searchParameters = getSearchParameters(
    searchText,
    searchFilters,
    pageNumber,
		"applications"
  );
  // check to make sure we are not trying to destructure undefined
  if (!searchParameters) {
    console.log("Search parameters are undefined");
    return;
  }

  const { parameters, searchType } = searchParameters;

  return await axios(`/api/applications/dashboard/search`, {
    method: "POST",
    require: "json",
    data: parameters,
    searchType: searchType,
    postTime: Date.now(),
  })
    .then((response) => {
      return formatApplicationResponse(response, searchText, searchType);
    })
    .catch((error) => {
      console.log(`API Error: /api/applications/dashboard/search/`, error);
      return false;
    });
}

function escapeSpecialCharacters(text) {
	return text.replace(/[-\\/\\^$*+?.()|[\]{}]/g, '\\$&');
}

function getNameSearchMatches(application, searchText, properties) {
	// create an array of names by splitting on the space
    var searchNames = searchText.split(" ");
		properties.forEach((name) => {
			// if the name or namecoapplicant exists
			if (application[name]) {
				// loop through each of the search names, sorted by longest search name
				searchNames
				.sort((a, b) => b.length - a.length)
				.forEach((searchName) => {
					var escapedSearch = escapeSpecialCharacters(searchName);
					var match = new RegExp(`(${escapedSearch})`, 'ig');

					// replace the matched text with empty html tags <></> surrounding it
					application[name] = application[name].replace(
						match,
						`<>$1</>`
						);
					});

					// replace all empty tags <></> with <span class='xhighlight'></span> to highlight the search terms in html
					application[name] = application[name]
						.replace(/<>/g, "<span class='xhighlight'>")
						.replace(/<\/>/g, "</span>");
				}
		});

		return application;
}


function getEmailSearchMatches(application, searchText, searchType) {
	//////////////////////////////////////////////////////////////////////////////////////////////////////
	// description: highlight email search matches in the application applicants' email addresses //
	//////////////////////////////////////////////////////////////////////////////////////////////////////

	var html = "";
	var matchCount = 0;

	// highlight the email search text for each applicant
	application.applicants.forEach((applicant) => {
		var encodedUri = encodeURIComponent(searchText);

		// create a regular expression to match the search text ('ig' means [case insensitve] [match all])
		var match = new RegExp(`(${encodedUri})`, "ig");

		if (
			!applicant.contactInfo ||
			match.test(
				encodeURIComponent(applicant.contactInfo?.emailAddress) || applicant.emailAddress || applicant.contactInfo?.emailAddress
			) === false
		) {
				return;
		}
		
		matchCount += 1;

		const escapedSearch = searchText.replace(/[-\\/\\^$*+?.()|[\]{}]/g, '\\$&');
		const regex = new RegExp(`(${escapedSearch})`, 'gi');

		// replace the matched text with empty html tags <></> surrounding it
		application.searchNotification = (
			applicant.emailAddress || applicant.contactInfo?.emailAddress
		).replace(regex, `<>$1</>`);

		// replace all empty tags <></> with <span class='xhighlight'></span> to highlight the search terms in html
		application.searchNotification = application.searchNotification
			.replace(
				/<>/g,
				`<span class='xhighlight'>`,
			)
			.replace(/<\/>/g, "</span>");

		// prefix with applicant type
		application.searchNotification = `<span class="xlabel">${applicant.applicantTypeName} Email:</span> ${application.searchNotification}`;

		html += `<li>${application.searchNotification}</li>`;
	});

	if (matchCount > 0) {
		application.searchNotification = null;
		application.searchNotifications = `<ul class="xsearch-email-matches">${html}</ul>`;
	}

	return application;
}

function getReferenceIdSearchMatches(application, searchText) {
	//////////////////////////////////////////////////////////////////////////////////////////////////////
	// description: highlight reference id search matches in the application reference id //
	//////////////////////////////////////////////////////////////////////////////////////////////////////
	var escapedSearch = escapeSpecialCharacters(searchText);

	// create a regular expression to match the search text ('ig' means [case insensitve] [match all])
	var match = new RegExp(`(${escapedSearch})`, 'ig');

	application.referenceIdSearchNotification =
		application.referenceId.replace(
			match,
			`<span class="xhighlight">$1</span>`,
		);

	return application;
}

function formatApplicationResponse(response, searchText, searchType) {
  //////////////////////////////////////////////////////////////////////////////////////////////////////
  // description: takes the application search response and formats each application for display //
  //////////////////////////////////////////////////////////////////////////////////////////////////////
  // make sure the applications data is not empty
  if (response.data.data.length > 0 && Array.isArray(response.data.data)) {
    // for each of the applications
    response.data.data.forEach((application) => {
      application.createdAtFormatted = application.createdAtUtc
        ? moment(`${application.createdAtUtc}Z`)
            .tz("America/Chicago")
            .format("MMM D, YYYY")
        : "No Date";

      application.applicants.forEach((applicant) => {
        // if this is the primary applicant
        if (applicant.applicantTypeId === 1) {
          // set the full name of the applicant (put the two names in an array, filter out the null names, and join with a space)
          application.name =
            [
              decodeHTMLEncodedStr(applicant.firstName),
              decodeHTMLEncodedStr(applicant.lastName),
            ]
              .filter((n) => n)
              .join(" ") || "No Name";

          // set the initials of the applicant
          application.initials = `${applicant.firstName?.charAt(0) || ""}${
            applicant.lastName?.charAt(0) || ""
          }`.trim();

          // if this is the secondary applicant
        } else if (applicant.applicantTypeId === 2) {
          application.nameCoApplicant = applicant.firstName
            ? `${decodeHTMLEncodedStr(
                applicant.firstName
              )} ${decodeHTMLEncodedStr(applicant.lastName)}`
            : "No Name";
        }
      });
      // if the lenders api has already loaded
      if (store.state.types["Lenders"]) {
        // for each of the lenders that matches the current application's lenderId
        store.state.types["Lenders"]
          .filter((lender) => lender.id == application.lenderId)
          .forEach((lender) => {
            // set the application lenderName
            application.defaultLenderName = lender.name;
          });
      }

      if (searchType) {
        // set the type of search
        application.searchCategory = "Application";

        // if the user is searching by generic text input
        if (response.config.searchType == "GenericTextInput") {
					// Get reference ID search matches
					application = getReferenceIdSearchMatches(application, searchText);

					// Get name search matches
					application = getNameSearchMatches(application, searchText, ["name", "nameCoApplicant"]);

					// Get email search matches
					application = getEmailSearchMatches(application, searchText,  response.config.searchType);
				}
				else if (response.config.searchType == "Name" || response.config.searchType == "Name Exact Match") {
          application = getNameSearchMatches(application, searchText, ["name", "nameCoApplicant"]);

          // if the user is searching by primary applicant name only
        } else if (
          response.config.searchType == "PrimaryApplicantNameExactMatch" ||
          response.config.searchType == "PrimaryApplicantName"
        ) {
					application = getNameSearchMatches(application, searchText, ["name"]);
          // if the user is searching by coapplicant name
        } else if (
					response.config.searchType == "CoApplicantNameExactMatch" ||
          response.config.searchType == "CoApplicantNamePartialMatch"
				) {
					application = getNameSearchMatches(application, searchText, ["nameCoApplicant"]);
				}
				// if the user is searching by application id
				else if (response.config.searchType == "Application ID") {
					application = getReferenceIdSearchMatches(application, searchText);

          // if the user is searching by ssn
        } else if (response.config.searchType == "SSN") {
          // highlight the ssn search text
          application.searchNotification = `SSN: <span class="xhighlight">${searchText}</span>`;
        } else if (response.config.searchType == "CLUID") {
          // highlight the cluid search text
          application.searchNotification = `CLUID: <span class="xhighlight">${searchText}</span>`;
        } else if (searchType === "Email") {
					application = getEmailSearchMatches(application, searchText,  response.config.searchType);
        } else if (searchType === "PrimaryApplicantEmail" || searchType === "PrimaryApplicantEmailPartialMatch") {
            // highlight email matches on primary applicant only
            var primaryApplicant = application.applicants.find(
              (applicant) =>
                applicant.applicantTypeId ===
                Number(ApplicantTypes.PRIMARY_APPLICANT),
            );

            if (primaryApplicant) {
              application.searchNotification = getMatchingEmail(primaryApplicant,searchText);
            }
				} else if (searchType === "CoApplicantEmail" || searchType === "CoApplicantEmailPartialMatch") {
            // highlight email matches on co-applicant only
            var coApplicant = application.applicants.find(
              (applicant) =>
                applicant.applicantTypeId ===
                Number(ApplicantTypes.CO_APPLICANT),
            );

          if (coApplicant) {
            application.searchNotification = getMatchingEmail(coApplicant,searchText);
          }
        }
      }
    });
  }

  return response;
}

function getMatchingEmail(applicant, searchText) {
  //////////////////////////////////////////////////////////////////////////////////////////////////////
  // description: returns the matching email with highlighted search text //
  //////////////////////////////////////////////////////////////////////////////////////////////////////
	var encodedUri = encodeURIComponent(searchText);

  var matchingEmail = null;

  // create a regular expression to match the search text ('ig' means [case insensitve] [match all])
  var match = new RegExp(`(${encodedUri})`, "ig");

  if (!applicant.contactInfo || match.test(
				encodeURIComponent(applicant.contactInfo?.emailAddress) || applicant.emailAddress || applicant.contactInfo?.emailAddress
			) === false) {
    return null;
  }
	
	const escapedSearch = searchText.replace(/[-\\/\\^$*+?.()|[\]{}]/g, '\\$&');
	const regex = new RegExp(`(${escapedSearch})`, 'gi');

		// replace the matched text with empty html tags <></> surrounding it
		matchingEmail = (
			applicant.emailAddress || applicant.contactInfo?.emailAddress
		).replace(regex, `<>$1</>`);

		// replace all empty tags <></> with <span class='xhighlight'></span> to highlight the search terms in html
		matchingEmail= matchingEmail
			.replace(
				/<>/g,
				`<span class='xhighlight'>`,
			)
			.replace(/<\/>/g, "</span>");

		// prefix with applicant type
		matchingEmail = `${applicant.applicantTypeName} Email: ${matchingEmail}`;

  return matchingEmail;
}

async function getLoans(searchText, searchFilters, pageNumber, searchQueries) {
  //////////////////////////////////////////////////////////////////////////////////
  // description: fetches loans by name, ssn, or loan id and formats the response //
  //////////////////////////////////////////////////////////////////////////////////

  var { parameters, searchType } = getSearchParameters(
    searchText,
    searchFilters,
    pageNumber,
		"loans"
  );

  if (pageNumber > 1) {
    parameters["PageNumber"] = pageNumber;
    parameters["pageSize"] = 30;
    parameters["page"] = {
      index: pageNumber,
      size: 30,
    };
  } else {
    parameters["pageSize"] = 30;
    parameters["page"] = {
      index: 1,
      size: 30,
    };
  }

  return await axios(`/api/loanservice/loans/filter`, {
    method: "POST",
    require: "json",
    data: parameters,
    searchType: searchType,
    postTime: Date.now(),
  })
    .then((response) => {
      // make sure the loan data is not empty
      if (
        response.data?.items?.length > 0 &&
        Array.isArray(response.data?.items)
      ) {
        // for each of the loans
        response.data.items.forEach((application) => {
          application.createdAtFormatted = application.createdAtUtc
            ? moment(`${application.createdAtUtc}Z`)
                .tz("America/Chicago")
                .format("MMM D, YYYY")
            : "No Date";

          application.borrowers?.forEach((borrower) => {
            // if this is the primary borrower
            if (borrower.typeId === Number(ApplicantTypes.PRIMARY_APPLICANT)) {
              // set the full name on the borrower
              application.name = borrower.firstName
                ? `${decodeHTMLEncodedStr(
                    borrower.firstName,
                  )} ${decodeHTMLEncodedStr(borrower.lastName)}`
                : "No Name";

              // set the initials of the borrower
              application.initials = `${borrower.firstName?.charAt(0) || ""}${
                borrower.lastName?.charAt(0) || ""
              }`.trim();
            }

						if (borrower.typeId === Number(ApplicantTypes.CO_APPLICANT)) {
              application.nameCoApplicant = borrower.firstName
                ? `${decodeHTMLEncodedStr(
                    borrower.firstName,
                  )} ${decodeHTMLEncodedStr(borrower.lastName)}`
                : "No Name";
						}
          });

          // if the lenders api has already loaded
          if (store.state.types["Lenders"]) {
            // for each of the lenders that matches the current application's lenderId
            store.state.types["Lenders"]
              .filter((lender) => lender.id == application.lenderId)
              .forEach((lender) => {
                // set the application lenderName
                application.lenderName = lender.name;
              });
          }

          if (searchType) {
            // set the type of search
            application.searchCategory = "Loan";

            // if the user is searching by name
            if (response.config.searchType == "Name" || response.config.searchType == "Name Exact Match") {
							application = getNameSearchMatches(application, searchText, ["name", "nameCoApplicant"]);

							// if they have a match on co-applicant
							if (application.nameCoApplicant && application.nameCoApplicant.includes('xhighlight')) {
								application.nameCoApplicantSearchNotification = application.nameCoApplicant;
							}						
							
              // if the user is searching by application id
            } else if (response.config.searchType == "Application ID") {
              // create a regular expression to match the search text ('ig' means [case insensitve] [match all])
              var match = new RegExp(`(${searchText})`, "ig");

              // replace the matched text with a span to highlight it
              application.searchNotification =
                "ID: " +
                application.referenceId.replace(
                  match,
                  `<span class="xhighlight">$1</span>`
                );

              // if the user is searching by ssn
            } else if (response.config.searchType == "SSN") {
              // highlight the ssn search text
              application.searchNotification = `SSN: <span class="xhighlight">${searchText}</span>`;
            } else if (response.config.searchType == "CLUID") {
              // highlight the cluid search text
              application.searchNotification = `CLUID: <span class="xhighlight">${searchText}</span>`;
						}
          }
        });
      }

      return response;
    })
    .catch((error) => {
      console.log(`API Error: /api/loanservice/loans/filter/`, error);
      return false;
    });
}

function objectToUrlString(parameters) {
  var finalUrl = "";

  Object.entries(parameters).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      // add to the final url as a string split by commas
      finalUrl += `${key}=[${value.map((value) => `"${value}"`).join(",")}]&`;
    } else {
      finalUrl += `${key}=${value}&`;
    }
  });

  return finalUrl.slice(0, -1);
}

async function logPageView(page) {
  ///////////////////////////////////////////////////
  // description: logs a page view to localStorage //
  ///////////////////////////////////////////////////

  var recentlyViewed = [];

  // if the localstore has any recently viewed pages
  if (localStorage.getItem("cache.RecentlyViewedPages")) {
    // retreive the data
    recentlyViewed = JSON.parse(
      localStorage.getItem("cache.RecentlyViewedPages"),
    );
  }

  // remove all entries which match the current id/page
  recentlyViewed = recentlyViewed.filter(
    (entry) => entry.id !== page.id || entry.page !== page.page,
  );

  // add the current page to the beginning of the array
  recentlyViewed.unshift(page);

  // add the data to localstorage
  localStorage.setItem(
    "cache.RecentlyViewedPages",
    JSON.stringify(recentlyViewed),
  );
}

function objectToHtmlTable(obj) {
  var html = "<table>";
  for (var key in obj) {
    var item = obj[key];
    var value =
      typeof item === "object"
        ? objectToHtmlTable(item)
        : item?.toString() || "";
    html += "<tr><td>" + key + "</td><td>" + value + "</tr>";
  }
  html += "</table>";
  return html;
}

function openModal(options) {
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // description: opens a modal (the component is '@/components/Modal.vue', included in @/components/layout/Page.vue) //
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  // close open modals
  store.state.modal.active = false;

  // open this modal
  store.state.modal.loading = false;
  store.state.modal.active = true;
  store.state.modal.title = options.title || options;
  store.state.modal.description = options.description || "";
  store.state.modal.html = options.html || "";
  store.state.modal.buttons = options.buttons || "";
  store.state.modal.fullscreen = options.fullscreen || false;
  store.state.modal.allowClickOutsideToClose =
    options.allowClickOutsideToClose !== undefined
      ? options.allowClickOutsideToClose
      : true;
}

function setModalLoading(flag) {
  store.state.modal.loading = flag || true;
}
function closeModal() {
  /////////////////////////////////
  // description: closes a modal //
  /////////////////////////////////

  store.state.modal.active = false;
}

function openPdfModal(options) {
  store.state.pdfModal.active = false;

  store.state.pdfModal.active = true;
  store.state.pdfModal.title = options.title || options;
  store.state.pdfModal.description = options.description || "";
  store.state.pdfModal.docId = options.docId.toString() || "";
  store.state.pdfModal.buttons = options.buttons || "";
  store.state.pdfModal.fullscreen = options.fullscreen || false;
  store.state.pdfModal.documents = options.documents || undefined;
  store.state.pdfModal.application = options.application || undefined;
  store.state.pdfModal.splitting = options.splitting || false;
}

function closePdfModal() {
  store.state.pdfModal.active = false;
}

// export the following functions
export default Object.assign(
  {},
  {
    capitalize,
    compareIncomeObjects,
    checkIncomeFormChanges,
    checkFormChanges,
    checkLocalStorage,
    cleanSearchText,
    closeModal,
    closePdfModal,
    compareObjects,
    displayProductType,
    getApplications,
    formatApplicationResponse,
    getLenderName,
    getLenders,
    getLoans,
    getSearchParameters,
    getType,
    isValidSSN,
    isValidCLUID,
    logPageView,
    objectToHtmlTable,
    objectToUrlString,
    openModal,
    openPdfModal,
    setModalLoading,
    phoneFormat,
    ssnFormat,
    formatFromUtc,
    getSchools,
    getSchoolsByProgram,
    summarizeFormChanges,
    buildFormChangesTable,
  },
);
